import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import authService from './api-authorization/AuthorizeService';
import * as UtilsGoogleMaps from './UtilsGoogleMaps';
import JobDetailsAddContactModalData from './JobDetailsAddContactModal';
import JobDetailsTransferJobModalData from './JobDetailsTransferJob';
import GooglePlacesDropdown from './GooglePlaces';
import { AddressDetails } from './models/JobInterfaces';
import { GoogleAddressItem } from './models/JobSharedInterfaces';

interface NewJobDetailProps {
    currentStep: number;
    jobDetails: JobDetails;
    siteContacts: SiteContacts;
    systemCompanyId: number;
    customers: CustomerList[];
    yesNo: GenericList[];
    jobTypes: GenericList[];
    statusOptions: GenericList[];
    branches: Branch[];
    copyJob: boolean;
    canReopenJob: boolean;
    canCancelJob: boolean;
    canUnlockJob: boolean;
    reloadJobDetails(): void;  //reload job details - call after transfer to another customer (not used when creating a new job)
    update(jobDetails: JobDetails, resetItems: boolean): void;
    updateSiteContacts(siteContacts: SiteContacts): void;
    updatePaymentType(accountTypeId: number): void;
    openNewJob(newJobId: number): void;     //used to open the new job when transferring the job to a new customer and restarting the job
    save(): void;
}

interface NewJobDetails {
    customerContacts: CustomerContactList[];
    useCustomerContact: boolean;
    addressDetails: AddressDetails;
    cancelReasons: GenericList[];
    cancelReasonId: number;
    showCancelModal: boolean;
    showAddContactModal: boolean;
    addContact: AddContact;
    canSendEmailToCustomer: boolean;
    showTransferJobModal: boolean;
    errors: any;
}

interface SiteContacts {
    siteContactName: string;
    siteContactPhone: string;
    siteContact2: string;
}

interface CustomerContactList {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    mobile: string;
}

interface JobDetails {
    id: number;
    companyId: number;
    copyJobId: number;
    customerId?: number;
    customer: string;
    customerExists: boolean;
    customerContactFirstName: string;
    customerContactLastName: string;
    customerContactPhone: string;
    customerContactEmail: string;
    customerAddress: string;
    customerStreetNumber: string;
    customerStreetName: string;
    customerSuburb: string;
    customerState: string;
    customerCountry: string;
    customerPostCode: string;
    customerGooglePlaceId: string;
    customerAddressLatitude: number;
    customerAddressLongitude: number;
    customerAbn: string;
    customerContactId?: number;
    customerCashSaleAccount: boolean;
    customerInvoiceQuotedAmount: boolean;
    customerChargeFuelSurcharge: boolean;
    customerNewFuelInvoice: boolean;
    useTyabbPricelist: boolean;
    sendEmailToCustomer: boolean;
    accountTypeId: number;
    accountType: string;
    accountLimitFormatted: string;
    customerAllowOnGoingHire: boolean;
    customerQuoteNotesDunny: string;
    siteAddressExists: boolean;
    siteAddress: string;
    siteAddressDistance: number;
    siteAddressDistanceFormatted: string;
    siteAddressDuration: number;
    siteAddressDurationFormatted: string;
    addressNearSiteAddress: string;
    unitNumber: string;
    streetNumber: string;
    streetName: string;
    suburb: string;
    state: string;
    country: string;
    postCode: string;
    manualStreetName: string;
    manualSuburb: string;
    manualState: string;
    manualCountry: string;
    manualPostCode: string;
    lotNumber: string;
    googlePlaceId: string;
    siteAddressLatitude: number;
    siteAddressLongitude: number;
    addressGooglePlaceId: string;
    addressSiteAddressLatitude: number;
    addressSiteAddressLongitude: number;
    addressSiteAddressDistance: number;
    addressSiteAddressDistanceFormatted: string;
    addressSiteAddressDuration: number;
    addressSiteAddressDurationFormatted: string;
    closestBranchId: number;
    isEvent: boolean;
    isThreeDayHire: boolean;
    eventName: string;
    eventStartDate: string;
    eventEndDate: string;
    deliveryWindowStart: string;
    deliveryWindowEnd: string;
    deliveryPlacementNotes: string;
    jobType: number;
    paymentType: number;
    gateCode: string;
    truckAccess: number;
    parking: number;
    siteClean: number;
    powerlinesWithinRange: number;
    weekdayAccess: number;
    weekendAccess: number;
    phoneCallRequired: number;
    statusId: number;
    notes: string;
    copyQuote: boolean;
    linkJob: boolean;
    linkedToReckon: boolean;
    locked: boolean;
    reference: string;
    priceOnly: boolean; //allows user just to get a price for the cusotmer not create a job
    goAheadOverride: boolean;   //allows the job to goahead even if the customer id locked
}

interface GenericList {
    id: number;
    name: string;
}

interface CustomerList {
    id: number;
    name: string;
    gateCode: string;
    accountId: string;
    accountIdDunny: string;
    accountTypeId: number;
    accountType: string;
    accountLimitFormatted: string;
    allowOnGoingHire: boolean;
    quoteNotesDunny: string;
    canEmailCustomer: boolean;
    cashSaleAccount: boolean;
    invoiceQuotedAmount: boolean;
    chargeFuelSurcharge: boolean;
    newFuelInvoice: boolean;
    statusTypeId: number;
    locked: boolean;
}

interface AddContact {
    customerId: number;
    customerContactId: number;  //for editing
}

interface Branch {
    id: number;
    name: string;
    fullAddress: string;
    latitude: number;
    longitude: number;
    scheduleBranchId: number;
}

export default class JobDetail extends React.Component<NewJobDetailProps, NewJobDetails> {
    constructor(props: NewJobDetailProps) {
        super(props);
        //update state from props
        this.state = {
            showCancelModal: false,
            showAddContactModal: false,
            customerContacts: [],
            useCustomerContact: false,
            cancelReasons: [],
            cancelReasonId: -1,
            errors: {},
            addressDetails: {
                fullAddress: "",
                street_number: "",
                route: "",
                locality: "",
                administrative_area_level_1: "",
                country: "",
                postal_code: "",
                latitude: 0,
                longitude: 0,
                placeId: ""
            },
            addContact: {
                customerId: -1,
                customerContactId: -1
            },
            canSendEmailToCustomer: true,
            showTransferJobModal: false
        }
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();

        //if we already have a customer id then need to load the customer contacts dropdown
        if (this.props.jobDetails.customerId) {
            var contactId = 0;
            if (this.props.jobDetails.customerContactId) {
                contactId = this.props.jobDetails.customerContactId;
            }
            //check if can send email
            var customer = this.props.customers.find(c => c.id == this.props.jobDetails.customerId);
            if (customer) {
                var canSendEmail = customer.canEmailCustomer;
                this.setState({ canSendEmailToCustomer: canSendEmail });
            }

            //get list of customer contacts from the database
            axios.get('api/Job/GetCustomerContact?customerId=' + this.props.jobDetails.customerId + '&customerContactId=' + contactId, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                this.setState({ customerContacts: res.data });
            })
        }

        //get the list of cancel reasons for any existing jobs 
        if (this.props.jobDetails.id > 0) {
            const user = await authService.getUser();
            axios.get('api/Job/GetCancelReasons?SubId=' + user.sub, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                this.setState({ cancelReasons: res.data })
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    validate = () => {
        let fields = this.props.jobDetails;
        let errors: any = {};
        let formIsValid = true;

        if (fields.id === 0 && fields.priceOnly) {
            //user is just getting a price for the customer
            return true;
        }

        if (fields.customerExists || fields.id > 0) {
            if (!fields.customerId || fields.customerId < 1) {
                formIsValid = false;
                errors["customerId"] = "Customer is required";
            }

            if (fields.customerCashSaleAccount) {
                //cash sales accounts use customer name, phone and email
                var contactName = (fields.customerContactFirstName + fields.customerContactLastName).trim();
                if (!contactName) {
                    formIsValid = false;
                    errors["customerContact"] = "Customer Contact First/Last Name is required";
                }
                if (!fields.customerContactPhone) {
                    formIsValid = false;
                    errors["customerContactPhone"] = "Customer Phone is required";
                }
                if (!fields.customerContactEmail) {
                    formIsValid = false;
                    errors["customerContactEmail"] = "Customer Email is required";
                } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                    formIsValid = false;
                    errors["customerContactEmail"] = "Valid Customer Contact Email is required";
                }
            } else {
                if (!fields.customerContactId || fields.customerContactId < 1) {
                    formIsValid = false;
                    errors["customerContactId"] = "Customer Contact is required";
                } else {
                    //need to make sure there is an email address for the contact
                    if (!fields.customerContactEmail) {
                        formIsValid = false;
                        errors["customerContactConfirmEmail"] = "Customer Contact Email is required";
                    } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                        formIsValid = false;
                        errors["customerContactConfirmEmail"] = "Valid Customer Contact Email is required";
                    }
                }
            }
        } else {
            //adding a new quoting customer so test company name, customer name, phone and email
            if (!fields.customer) {
                formIsValid = false;
                errors["customer"] = "Customer Company Name is required";
            }
            if (!fields.customerContactFirstName || !fields.customerContactLastName) {
                formIsValid = false;
                errors["customerContact"] = "Customer Contact First/Last Name is required";
            }
            if (!fields.customerAddress || !fields.customerAddress) {
                formIsValid = false;
                errors["customerAddress"] = "Customer Address is required";
            }
            if (!fields.customerContactPhone) {
                formIsValid = false;
                errors["customerContactPhone"] = "Customer Phone is required";
            }
            if (!fields.customerContactEmail) {
                formIsValid = false;
                errors["customerContactEmail"] = "Customer Email is required";
            } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                formIsValid = false;
                errors["customerContactEmail"] = "Valid Customer Contact Email is required";
            }
        }
        

        if (fields.siteAddressExists && !fields.siteAddress) {
            formIsValid = false;
            errors["siteAddress"] = "Valid Site Address is required";
        } else if (!fields.siteAddressExists) {
            if (!fields.manualStreetName) {
                formIsValid = false;
                errors["manualStreetName"] = "Manual Street Name is required";
            }
            if (!fields.manualSuburb) {
                formIsValid = false;
                errors["manualSuburb"] = "Manual Suburb is required";
            }
            if (!fields.manualState) {
                formIsValid = false;
                errors["manualState"] = "Manual State is required";
            }
        }

        if (fields.isEvent || fields.isThreeDayHire) {
            if (!fields.eventName || fields.eventName === "") {
                formIsValid = false;
                var fieldName = fields.isEvent ? "Event " : "Invoicee ";
                errors["eventName"] = fieldName + "Name is required";
            }

            //Add validation back in when we switch on extra event info
            //if (!fields.eventStartDate || fields.eventStartDate === "") {
            //    formIsValid = false;
            //    errors["eventStartDate"] = "Event Start Date is required";
            //} else if (!DateHelpers.isValidDate(fields.eventStartDate)) {
            //    formIsValid = false;
            //    errors["eventStartDate"] = "Valid Event Start Date is required";
            //}

            //if (!fields.eventEndDate || fields.eventEndDate === "") {
            //    formIsValid = false;
            //    errors["eventEndDate"] = "Event End Date is required";
            //} else if (!DateHelpers.isValidDate(fields.eventEndDate)) {
            //    formIsValid = false;
            //    errors["eventEndDate"] = "Valid Event End Date is required";
            //} else if (!DateHelpers.compareDateGreater(fields.eventStartDate, fields.eventEndDate)) {
            //    formIsValid = false;
            //    errors["eventEndDate"] = "Event End Date > Event Start Date";
            //}

            //if (!fields.deliveryWindowStart || fields.deliveryWindowStart === "") {
            //    formIsValid = false;
            //    errors["deliveryWindowStart"] = "Delivery Window Start is required";
            //} else if (!DateHelpers.isValidDate(fields.deliveryWindowStart)) {
            //    formIsValid = false;
            //    errors["deliveryWindowStart"] = "Valid Delivery Window Start is required";
            //}

            //if (!fields.deliveryWindowEnd || fields.deliveryWindowEnd === "") {
            //    formIsValid = false;
            //    errors["deliveryWindowEnd"] = "Delivery Window End is required";
            //} else if (!DateHelpers.isValidDate(fields.deliveryWindowEnd)) {
            //    formIsValid = false;
            //    errors["deliveryWindowEnd"] = "Valid Delivery Window End is required";
            //} else if (!DateHelpers.compareDateGreater(fields.deliveryWindowStart, fields.deliveryWindowEnd)) {
            //    formIsValid = false;
            //    errors["deliveryWindowEnd"] = "Delivery Window End > Delivery Window Start";
            //}
        }

        if (fields.jobType <= 0) {
            formIsValid = false;
            errors["jobType"] = "Job Type is required";
        }

        if (fields.jobType === 2 && fields.id === 0) {
            //Need to enter Site Contact details if creating a new Pricelist job
            if (!this.props.siteContacts.siteContactName) {
                formIsValid = false;
                errors["siteContactName"] = "Site Contact is required";
            }

            if (!this.props.siteContacts.siteContactPhone) {
                formIsValid = false;
                errors["siteContactPhone"] = "Site Contact Phone is required";
            }
        }

        if (this.props.systemCompanyId === 2) {
            if (fields.truckAccess < 0) {
                formIsValid = false;
                errors["truckAccess"] = "Truck Access is required";
            }

            if (fields.parking < 0) {
                formIsValid = false;
                errors["parking"] = "Parking Available is required";
            }

            if (fields.siteClean < 0) {
                formIsValid = false;
                errors["siteClean"] = "Is Site Clean is required";
            }
        } else if (this.props.systemCompanyId === 3) {
            if (fields.weekdayAccess < 0) {
                formIsValid = false;
                errors["weekdayAccess"] = "Weekday Access is required";
            }

            if (fields.weekendAccess < 0) {
                formIsValid = false;
                errors["weekendAccess"] = "Weekend Access is required";
            }

            if (fields.phoneCallRequired < 0) {
                formIsValid = false;
                errors["phoneCallRequired"] = "Phone Call is required";
            }
        }

        if (fields.powerlinesWithinRange < 0) {
            formIsValid = false;
            errors["powerlinesWithinRange"] = "Powerlines within 4.6m is required";
        }

        if (!fields.siteAddressExists && fields.addressSiteAddressDuration === 0) {
            formIsValid = false;
            errors["addressSiteAddressDuration"] = "Mins to Site Address required";
        }

        this.setState({ errors: errors });
        return formIsValid;
    }

    contactSubmit = (e: any) => {
        e.preventDefault();
        if (this.validate()) {
            this.submit();
        } else {
            toast.error("Check and fix errors before saving")
        }

    }

    submit = () => {
        this.props.save();
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let jobDetails = this.props.jobDetails;
        let change: any = {};
        change = jobDetails;
        change[e.target.name] = e.target.value;
        this.setState({ errors: errors })
        this.props.update(change, false);
    }

    handleSiteContactChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let siteCont = this.props.siteContacts;
        let change: any = {};
        change = siteCont;
        change[e.target.name] = e.target.value;
        this.setState({ errors: errors })
        this.props.updateSiteContacts(change);
    }

    handleUseCustomerContactChange = (e: any) => {
        this.setState({ useCustomerContact: e.target.checked });
        if (e.target.checked) {
            let siteCont = this.props.siteContacts;
            siteCont.siteContactName = this.props.jobDetails.customerContactFirstName + " " + this.props.jobDetails.customerContactLastName;
            siteCont.siteContactPhone = this.props.jobDetails.customerContactPhone;
            this.props.updateSiteContacts(siteCont);
        }
    }

    handleCustomerContactChange = (e: any) => {
        e.preventDefault();
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
            this.setState({ errors: errors })
        }

        var customerContactId = e.target.value * 1;

        let jobDetails = this.props.jobDetails;
        jobDetails.customerContactId = customerContactId;
        this.props.update(jobDetails, false);

        if (customerContactId > 0) {
            //check to see if there is an email address, and if not then show edit contact box so user can add email
            var contact = this.state.customerContacts.find(cc => cc.id == customerContactId);
            if (contact) {
                jobDetails.customerContactFirstName = contact.firstName;
                jobDetails.customerContactLastName = contact.lastName;
                jobDetails.customerContactPhone = contact.mobile == "" ? contact.phone : contact.mobile;
                jobDetails.customerContactEmail = contact.email;
                this.props.update(jobDetails, false);
                //var email = contact.email;
                //if (!email || email.indexOf("@") < 0 || jobDetails.jobType === 2 || jobDetails.jobType === 3) {
                //    //need to enter email address
                //    this.editContactModal();
                //}
            }
        }
    }

    handleCheckboxChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let jobDetails = this.props.jobDetails;

        if ((e.target.name === "isEvent" || e.target.name === "isThreeDayHire" || (jobDetails.priceOnly && e.target.name === "useTyabbPricelist")) && jobDetails.id === 0) {
            //need to reset customer exists if they untick is event
            jobDetails.customerExists = true;
            jobDetails.customerId = -1;
            jobDetails.customerContactId = -1;
            jobDetails.customer = "";
            jobDetails.customerContactFirstName = "";
            jobDetails.customerContactLastName = "";
            jobDetails.customerContactPhone = "";
            jobDetails.customerContactEmail = "";
        } else if (e.target.name === "priceOnly") {
            jobDetails.customerExists = true;
        }

        if (e.target.name === "isEvent" || e.target.name === "isThreeDayHire") {
            //don't want both selected
            if (e.target.name === "isEvent") {
                jobDetails.isThreeDayHire = false;
            } else if (e.target.name === "isThreeDayHire") {
                jobDetails.isEvent = false;
            }
        }

        let change: any = {};
        change = jobDetails;
        change[e.target.name] = e.target.checked;
        this.setState({ errors: errors });

        if (!jobDetails.isEvent && !jobDetails.isThreeDayHire) {
            //reset event fields
            jobDetails.eventStartDate = "";
            jobDetails.eventEndDate = "";
            jobDetails.deliveryWindowStart = "";
            jobDetails.deliveryWindowEnd = "";
        }

        this.props.update(change, false);
    }

    handleAddressExistsChange = (e: any) => {
        //clear site address details when switching
        var jobDetails = this.props.jobDetails;
        jobDetails.siteAddressExists = !e.target.checked;
        jobDetails.siteAddress = "";
        jobDetails.siteAddressDistance = 0;
        jobDetails.siteAddressDistanceFormatted = "";
        jobDetails.siteAddressDuration = 0;
        jobDetails.siteAddressDurationFormatted = "";
        jobDetails.streetNumber = "";
        jobDetails.streetName = "";
        jobDetails.suburb = "";
        jobDetails.state = "";
        jobDetails.country = "";
        jobDetails.postCode = "";
        jobDetails.manualStreetName = "";
        jobDetails.manualSuburb = "";
        jobDetails.manualState = "VIC";
        jobDetails.manualCountry = "Australia";
        jobDetails.manualPostCode = "";
        this.props.update(jobDetails, false);
    }

    handleCustomerExistsChange = (e: any) => {
        var jobDetails = this.props.jobDetails;
        jobDetails.customerExists = !e.target.checked;
        jobDetails.customerId = -1;
        jobDetails.customerContactId = -1;
        jobDetails.customer = "";
        jobDetails.customerContactFirstName = "";
        jobDetails.customerContactLastName = "";
        jobDetails.customerContactPhone = "";
        jobDetails.customerContactEmail = "";
        jobDetails.gateCode = "";
        jobDetails.accountTypeId = 1;
        jobDetails.accountType = "Prepaid";
        jobDetails.accountLimitFormatted = "";
        jobDetails.customerAllowOnGoingHire = jobDetails.companyId === 3 ? true : false;
        jobDetails.customerQuoteNotesDunny = "";
        jobDetails.linkedToReckon = false;
        jobDetails.customerCashSaleAccount = false;
        jobDetails.locked = false;

        this.props.update(jobDetails, false);
    }

    customerChanged = (e: any) => {
        var customerId = e.target.value * 1;
        this.getCustomerContact(customerId);
    }

    async getCustomerContact(customerId: number) {
        const token = await authService.getAccessToken();

        var errors = this.state.errors;
        if (customerId) {
            delete errors["customerId"];
        }

        var jobDetails = this.props.jobDetails;
        jobDetails.customerId = customerId;
        jobDetails.customerContactId = -1;
        jobDetails.useTyabbPricelist = false;

        //set the gate code
        if (customerId >= 0) {
            var customer = this.props.customers.find(c => c.id == customerId);
            if (customer) {
                jobDetails.gateCode = customer.gateCode ?? "";
                jobDetails.accountTypeId = customer.accountTypeId;
                jobDetails.accountType = customer.accountType;
                jobDetails.accountLimitFormatted = customer.accountLimitFormatted;
                jobDetails.customerAllowOnGoingHire = customer.allowOnGoingHire;
                jobDetails.customerQuoteNotesDunny = customer.quoteNotesDunny ?? "";
                jobDetails.locked = customer.locked;
                jobDetails.customer = customer.name;
                jobDetails.customerContactId = -1;
                jobDetails.customerContactFirstName = "";
                jobDetails.customerContactLastName = "";
                jobDetails.customerContactPhone = "";
                jobDetails.customerContactEmail = "";
                jobDetails.sendEmailToCustomer = customer.canEmailCustomer;
                jobDetails.linkedToReckon = customer.accountIdDunny != "";
                jobDetails.customerCashSaleAccount = customer.cashSaleAccount;
                jobDetails.customerInvoiceQuotedAmount = customer.invoiceQuotedAmount;
                jobDetails.customerChargeFuelSurcharge = customer.chargeFuelSurcharge;
                jobDetails.customerNewFuelInvoice = customer.newFuelInvoice;
                this.setState({ canSendEmailToCustomer: customer.canEmailCustomer });

                this.props.updatePaymentType(customer.accountTypeId);
            } else {
                jobDetails.accountType = "";
                jobDetails.accountLimitFormatted = "";
            }
        }

        //get list of customer contacts from the database
        axios.get('api/Job/GetCustomerContact?customerId=' + customerId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ customerContacts: res.data });
            //if we are using a cash sales customer then set the customer contact to the first contact in the list - should be an SS contact person
            if (jobDetails.customerCashSaleAccount && res.data.length > 0) {
                var firstContact = res.data[0]; //should only be one
                jobDetails.customerContactId = firstContact.id;
            }
            this.props.update(jobDetails, true);
        })
    }

    setAddress = async (addressDetails: AddressDetails) => {
        let jobDetails = this.props.jobDetails;
        jobDetails.siteAddress = addressDetails.fullAddress;
        jobDetails.streetNumber = addressDetails.street_number;
        jobDetails.streetName = addressDetails.route;
        jobDetails.suburb = addressDetails.locality;
        jobDetails.state = addressDetails.administrative_area_level_1;
        jobDetails.country = addressDetails.country;
        jobDetails.postCode = addressDetails.postal_code;
        jobDetails.googlePlaceId = addressDetails.placeId;
        jobDetails.siteAddressLatitude = addressDetails.latitude;
        jobDetails.siteAddressLongitude = addressDetails.longitude;

        var branchLocations = this.props.branches; 
        var existingDuration = jobDetails.siteAddressDuration;
        var shortestDuration = 0;
        var shortestDistance = 0;
        var branchId = 1;   //Default to Bayswater

        for (var b = 0; b < branchLocations.length; b++) {
            var branch = branchLocations[b];

            var branchAddrLatLng: GoogleAddressItem = {
                lat: branch.latitude,
                lng: branch.longitude,
                fullAddress: branch.fullAddress
            };

            var siteAddrLatLng: GoogleAddressItem = {
                lat: addressDetails.latitude,
                lng: addressDetails.longitude,
                fullAddress: addressDetails.fullAddress
            };

            var result = await UtilsGoogleMaps.calculateDistance(branchAddrLatLng, siteAddrLatLng);
            if (result) {
                if (result && (shortestDuration === 0 || shortestDuration > result.duration)) {
                    shortestDuration = result.duration;
                    shortestDistance = result.distance;
                    branchId = branch.id;
                }
            }

            var mins = shortestDuration / 60;
            if (mins <= 45) {
                //within the city limits so don't need to check the rest of the addresses
                break;
            }
        }

        var shortestDist = shortestDistance / 1000;
        var shortestDur = shortestDuration / 60;

        jobDetails.siteAddressDistance = shortestDist;
        jobDetails.siteAddressDistanceFormatted = shortestDist + " km";
        jobDetails.siteAddressDuration = shortestDur;
        jobDetails.siteAddressDurationFormatted = shortestDur + " mins";
        jobDetails.closestBranchId = branchId;

        //only reset the quote if travel time is more than 90 mins as that re-calculates the travel amount
        var resetQuote = existingDuration > 90 || shortestDur > 90;
        this.props.update(jobDetails, resetQuote);
    }

    setNearestAddress = async (addressDetails: AddressDetails) => {
        let jobDetails = this.props.jobDetails;
        jobDetails.addressNearSiteAddress = addressDetails.fullAddress;
        jobDetails.addressGooglePlaceId = addressDetails.placeId;
        jobDetails.addressSiteAddressLatitude = addressDetails.latitude;
        jobDetails.addressSiteAddressLongitude = addressDetails.longitude;

        var branchLocations = this.props.branches; 
        var shortestDuration = 0;
        var shortestDistance = 0;
        var branchId = 1;   //default to Bayswater

        for (var b = 0; b < branchLocations.length; b++) {
            var branch = branchLocations[b];

            var branchAddrLatLng: GoogleAddressItem = {
                lat: branch.latitude,
                lng: branch.longitude,
                fullAddress: branch.fullAddress
            };

            var siteAddrLatLng: GoogleAddressItem = {
                lat: addressDetails.latitude,
                lng: addressDetails.longitude,
                fullAddress: addressDetails.fullAddress
            };

            var result = await UtilsGoogleMaps.calculateDistance(branchAddrLatLng, siteAddrLatLng);
            if (result && (shortestDuration === 0 || shortestDuration > result.duration)) {
                shortestDuration = result.duration;
                shortestDistance = result.distance;
                branchId = branch.id;
            };

            var mins = shortestDuration / 60;
            if (mins <= 45) {
                //within the city limits so don't need to check the rest of the addresses
                break;
            }
        }

        var shortestDist = shortestDistance / 1000;
        var shortestDur = shortestDuration / 60;

        jobDetails.addressSiteAddressDistance = shortestDist;
        jobDetails.addressSiteAddressDistanceFormatted = shortestDist + " km";
        jobDetails.addressSiteAddressDuration = shortestDur;
        jobDetails.addressSiteAddressDurationFormatted = shortestDur + " mins";
        jobDetails.closestBranchId = branchId;

        this.props.update(jobDetails, false);
    }

    setCustomerAddress = async (updatedAddress: AddressDetails) => {
        let jobDetails = this.props.jobDetails;

        jobDetails.customerAddress = updatedAddress.fullAddress;
        jobDetails.customerStreetNumber = updatedAddress.street_number;
        jobDetails.customerStreetName = updatedAddress.route;
        jobDetails.customerSuburb = updatedAddress.locality;
        jobDetails.customerState = updatedAddress.administrative_area_level_1;
        jobDetails.customerCountry = updatedAddress.country;
        jobDetails.customerPostCode = updatedAddress.postal_code;
        jobDetails.customerGooglePlaceId = updatedAddress.placeId;
        jobDetails.customerAddressLatitude = updatedAddress.latitude;
        jobDetails.customerAddressLongitude = updatedAddress.longitude;

        this.props.update(jobDetails, false);
    } 

    hideCancelModal = () => {
        this.setState({ showCancelModal: false });
    }

    showCancelModal = () => {
        //show popup to select reason
        this.setState({ showCancelModal: true });
    }

    handleCancelReasonChange = (e: any) => {
        var reasonId = e.target.value;
        this.setState({ cancelReasonId: reasonId });
    }

    saveCancel = () => {
        var errors = this.state.errors;
        if (this.state.cancelReasonId < 1) {
            errors["cancelReasonId"] = "Please select a reason";
            this.setState({ errors: errors });
        } else {
            this.cancelJob();
        }
    }

    async cancelJob() {
        var errors = this.state.errors;
        delete errors["cancelReasonId"];
        this.setState({ errors: errors, showCancelModal: false });

        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //update job status
        var cancelJob = {
            jobId: this.props.jobDetails.id,
            cancelReasonId: this.state.cancelReasonId,
            subId: user.sub
        };
        var jobDetails = this.props.jobDetails;

        axios.post('api/Job/CancelJob', cancelJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            jobDetails.statusId = 7;
            this.props.update(jobDetails, false);

            toast.success("Job Cancelled");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    reopenJob = (e: any) => {
        e.preventDefault();
        this.reopenJobDetails();
    }

    async reopenJobDetails() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var openJob = {
            jobId: this.props.jobDetails.id,
            subId: user.sub
        };
        axios.post('api/Job/ReopenJob', openJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var jobDetails = this.props.jobDetails;
            jobDetails.statusId = res.data;
            this.props.update(jobDetails, false);
            toast.success("Job Re-opened Successfully");
        })
        .catch(error => {
            var errorMessage = error.response.data;
            if (!errorMessage) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        });
    }

    hideAddContactModal = () => {
        var contact = this.state.addContact;
        contact.customerId = -1;
        contact.customerContactId = -1;

        this.setState({ addContact: contact, showAddContactModal: false });
    }

    showAddContactModal = (e: any) => {
        e.preventDefault();
        //show popup to add contact
        if (this.props.jobDetails.customerId && this.props.jobDetails.customerId > 0) {
            var contact = this.state.addContact;
            contact.customerId = this.props.jobDetails.customerId;
            contact.customerContactId = -1;
            this.setState({ addContact: contact, showAddContactModal: true });
        } else {
            toast.error("Please select a Customer before adding a Contact");
        }
    }

    showEditContactModal = (e: any) => {
        e.preventDefault();
        //show popup to edit contact
        if (this.props.jobDetails.customerId && this.props.jobDetails.customerId > 0) {
            this.editContactModal();
        } else {
            toast.error("Please select a Customer before editing a Contact");
        }
    }

    editContactModal = () => {
        var contactId = (this.props.jobDetails.customerContactId ?? -1) * 1;

        var contact = this.state.addContact;
        contact.customerId = (this.props.jobDetails.customerId ?? -1) * 1;
        contact.customerContactId = contactId;

        this.setState({ addContact: contact, showAddContactModal: true });
    }

    saveAddContact = (contactDetails: CustomerContactList) => {
        var contact = this.state.addContact;
        var contactList = this.state.customerContacts;
        var jobDetails = this.props.jobDetails;
        jobDetails.customerContactId = contact.customerContactId;

        if (contact.customerContactId > 0) {
            //update customer details
            var existContact = contactList.find(cc => cc.id == contact.customerContactId);
            if (existContact) {
                existContact.name = contactDetails.name;
            }
        } else {
            //add the new contact details to the list
            contactList.push(contactDetails);
            //when we add a new contact then auto update email address as they had to enter it when creating the contact
            jobDetails.customerContactEmail = contactDetails.email;
        }

        //reset add contact
        contact.customerId = -1;
        contact.customerContactId = -1;

        this.setState({ addContact: contact, customerContacts: contactList, showAddContactModal: false });
        this.props.update(jobDetails, false);
    }

    //Transfer Job
    showTransferModal = (e: any) => {
        e.preventDefault();
        this.setState({ showTransferJobModal: true });
    }

    hideTransferJobModal = () => {
        this.setState({ showTransferJobModal: false });
    }

    saveTransferJobModal = (updatedCustomerContacts: CustomerContactList[], jobId: number) => {
        this.setState({ showTransferJobModal: false });
        toast.success("Job has been transferred successfully!");

        //stay on this job and reload contacts as they have changed to new customer
        if (updatedCustomerContacts.length > 0) {
            //we only what to update the customer contacts if we update the current job
            this.setState({ customerContacts: updatedCustomerContacts });
            this.props.reloadJobDetails();
        } else {
            //this job has been cancelled so open new job
            this.props.openNewJob(jobId);
        }
    }
    //End Transfer Job

    render() {
        if (this.props.currentStep !== 1) {
            return null;
        }

        var existingJob = false;
        if (this.props.jobDetails.id) {
            existingJob = true;
        }

        let jobDetails = this.props.jobDetails;
        var yesNoOptions = this.props.yesNo;
        var jobTypeOptions = this.props.jobTypes;
        var statusOptions = this.props.statusOptions;
        
        var customerOptions = this.props.customers;
        var customerContactOptions = this.state.customerContacts;
        var cancelReasonOptions = this.state.cancelReasons;
        var buttonName = existingJob ? "Save" : "Next";

        var googleContents = <GooglePlacesDropdown addressId="autocomplete" addressLabel={"Site Address"} placeholder={jobDetails.siteAddress} className="input-group" useLargeLabel={false} afterValid={this.setAddress} />
        var googleAddressNearSite = <GooglePlacesDropdown addressId="autocompletenearsite" addressLabel={"Address Near Site"} placeholder={jobDetails.addressNearSiteAddress} className="input-group" useLargeLabel={false} afterValid={this.setNearestAddress} />
        var customerAddress = <GooglePlacesDropdown addressId="autocompletecustomer" addressLabel={"Customer Address"} placeholder={jobDetails.customerAddress} className="input-group" useLargeLabel={false} afterValid={this.setCustomerAddress} />
        var addEditContact = <JobDetailsAddContactModalData showModal={this.state.showAddContactModal} hideModal={this.hideAddContactModal} customerId={this.state.addContact.customerId} id={this.state.addContact.customerContactId} saveModal={this.saveAddContact} />
        var transferJob = <JobDetailsTransferJobModalData showModal={this.state.showTransferJobModal} hideModal={this.hideTransferJobModal} jobId={this.props.jobDetails.id} systemCompanyId={this.props.systemCompanyId} customers={this.props.customers} saveModal={this.saveTransferJobModal} />

        return (
            <div>
                <div className="static-modal">
                    {addEditContact}
                    {transferJob}
                </div>
                <div className="static-modal">
                    <Modal aria-labelledby="contained-modal-title-sm" show={this.state.showCancelModal} onHide={this.hideCancelModal}>
                        <Modal.Header className="modal__header--colour">
                            <Modal.Title>Cancel Job</Modal.Title>
                            <button type="button" className="close" onClick={this.hideCancelModal}>&times;</button>
                        </Modal.Header>
                        <Modal.Body>
                            <label className="input-group" htmlFor="cancelReasonId">
                                <span className="label">Cancel Reason</span>
                                <select className="select fullWidth" id="cancelReasonId" name="cancelReasonId" value={this.state.cancelReasonId} onChange={(e) => this.handleCancelReasonChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {cancelReasonOptions.map(reason =>
                                        <option key={reason.id} value={reason.id}>{reason.name}</option>
                                    )};
                                    </select>
                            </label>
                            <span className="label errors">{this.state.errors["cancelReasonId"]}</span>
                        </Modal.Body>

                        <Modal.Footer>
                            <button className="popup-button--yes" onClick={this.saveCancel}>Save</button>
                        </Modal.Footer>
                    </Modal>
                </div>

                <div className="columngroup">
                    <div className="columnleft">
                        <div className={this.props.systemCompanyId === 3 && jobDetails.id === 0 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="priceOnly">
                                <span className="label">Get Price ONLY?<i className="fa-regular fa-circle-question edit--icon marginLeft10" title="Allows you to get a price for the customer without creating a job."></i></span>
                                <input className="checkbox" type="checkbox" id="priceOnly" name="priceOnly" checked={jobDetails.priceOnly} onChange={(e) => this.handleCheckboxChange(e)}></input>
                            </label>
                            <hr></hr>
                            <label className={jobDetails.priceOnly ? "input-group" : "hidden"} htmlFor="useTyabbPricelist">
                                <span className="label">Use RAB/PTH Pricelist?</span>
                                <input className="checkbox" type="checkbox" id="useTyabbPricelist" name="useTyabbPricelist" checked={jobDetails.useTyabbPricelist} onChange={(e) => this.handleCheckboxChange(e)}></input>
                            </label>
                        </div>
                        <div className={jobDetails.priceOnly ? "hidden" : ""}>
                            <div>
                                <label className={this.props.systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="isEvent">
                                    <span className="label">Is job for an Event?</span>
                                    <input className="checkbox" type="checkbox" id="isEvent" name="isEvent" checked={jobDetails.isEvent} onChange={(e) => this.handleCheckboxChange(e)}></input>
                                </label>

                                <label className={this.props.systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="isThreeDayHire">
                                    <span className="label">Or Short-Term Hire?</span>
                                    <input className="checkbox" type="checkbox" id="isThreeDayHire" name="isThreeDayHire" checked={jobDetails.isThreeDayHire} onChange={(e) => this.handleCheckboxChange(e)}></input>
                                </label>
                            </div>
                            <div className={jobDetails.isEvent || jobDetails.isThreeDayHire ? "" : "hidden"}>
                                <label className="input-group" htmlFor="eventName">
                                    <span className="label">{jobDetails.isEvent ? "Event Name" : jobDetails.isThreeDayHire ? "Invoicee" : ""}</span>
                                    <input className="input" type="text" id="eventName" name="eventName" value={jobDetails.eventName} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["eventName"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["eventName"]}</span>
                            </div>
                            <div className="hidden">
                                <label className="input-group" htmlFor="eventStartDate">
                                    <span className="label">Event Start</span>
                                    <input className="input" type="datetime-local" id="eventStartDate" name="eventStartDate" value={jobDetails.eventStartDate} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["eventStartDate"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["eventStartDate"]}</span>
                                <label className="input-group" htmlFor="eventEndDate">
                                    <span className="label">Event End</span>
                                    <input className="input" type="datetime-local" id="eventEndDate" name="eventEndDate" value={jobDetails.eventEndDate} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["eventEndDate"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["eventEndDate"]}</span>
                                <div className="marginLeftLabel">Enter the earliest date and time that the product/s can be delivered.</div>
                                <label className="input-group" htmlFor="deliveryWindowStart">
                                    <span className="label">Delivery Window Start</span>
                                    <input className="input" type="datetime-local" id="deliveryWindowStart" name="deliveryWindowStart" value={jobDetails.deliveryWindowStart} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["deliveryWindowStart"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["deliveryWindowStart"]}</span>
                                <div className="marginLeftLabel">Enter the latest date and time that the product/s can be delivered.</div>
                                <label className="input-group" htmlFor="deliveryWindowEnd">
                                    <span className="label">Delivery Window End</span>
                                    <input className="input" type="datetime-local" id="deliveryWindowEnd" name="deliveryWindowEnd" value={jobDetails.deliveryWindowEnd} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["deliveryWindowEnd"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["deliveryWindowEnd"]}</span>
                                <hr></hr>
                            </div>
                            <label className={jobDetails.id === 0 ? "input-group" : "hidden"} htmlFor="customerExists">
                                <span className="label label--setsizesm">Customer Doesn't Exist</span>
                                <input className="checkbox" type="checkbox" id="customerExists" name="customerExists" checked={!jobDetails.customerExists} onChange={(e) => this.handleCustomerExistsChange(e)}></input>
                            </label>
                        </div>
                        <div className={jobDetails.id === 0 && !jobDetails.customerExists ? "hidden" : ""}>
                            <label className="input-group" htmlFor="customer">
                                <span className="label">Customer<span className={jobDetails.priceOnly ? "makeitred marginLeft10" : "hidden"}>OPTIONAL</span></span>
                                <select className={jobDetails.id == 0 ? "select marginRight10" : "hidden"} id="customer" name="customerId" value={jobDetails.customerId} onChange={this.customerChanged}>
                                    <option hidden defaultValue="-1"></option>
                                    {customerOptions.map(cust =>
                                        <option key={cust.id} value={cust.id}>{cust.name}</option>
                                    )};
                                </select>
                                <input className={jobDetails.id == 0 ? "hidden" : "input marginRight10"} type="text" id="customer" name="customer" value={jobDetails.customer} disabled></input>
                                <a className={jobDetails.customerId && jobDetails.customerId > 0 ? "marginRight10" : "hidden"} href={"/customer/" + jobDetails.customerId} target="_blank">View</a>
                                <a className={jobDetails.id > 0 && jobDetails.customerId && jobDetails.customerId > 0 ? "" : "hidden"} href="#" onClick={(e) => this.showTransferModal(e)}>Transfer</a>
                            </label>
                            <span className={jobDetails.accountLimitFormatted === "" ? "hidden" : "marginLeftLabel"}>{jobDetails.accountTypeId === 1 ? jobDetails.accountType : jobDetails.accountType + ' - ' + jobDetails.accountLimitFormatted}</span>
                            <span className={jobDetails.locked ? "makeitred makeitbold marginLeft20" : "hidden"}>Customer has been LOCKED by Accounts!</span>
                            <div className={!jobDetails.linkedToReckon && jobDetails.jobType === 2 && jobDetails.id === 0 ? "makeitred makeitbold marginLeftLabel" : "hidden"}>Let Accounts know that the customer needs to be linked to Reckon.</div>
                            <span className={this.state.errors["customerId"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerId"]}</span>
                        </div>
                        <div className={jobDetails.customerCashSaleAccount || (jobDetails.id === 0 && (!jobDetails.customerExists || jobDetails.priceOnly)) ? "hidden" : ""}>
                            <label className="input-group" htmlFor="contact">
                                <span className="label">Contact</span>
                                <select className="select marginRight10" id="contact" name="customerContactId" value={jobDetails.customerContactId} onChange={(e) => this.handleCustomerContactChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {customerContactOptions.map(custcon =>
                                        <option key={custcon.id} value={custcon.id}>{custcon.name}</option>
                                    )};
                                </select>
                                <a className={jobDetails.customerContactId && jobDetails.customerContactId > 0 ? "marginRight10" : "hidden"} href="#" onClick={(e) => this.showEditContactModal(e)}>Edit</a>
                                <a className={jobDetails.customerId && jobDetails.customerId > 0 ? "" : "hidden"} href="#" onClick={(e) => this.showAddContactModal(e)}>Add New</a>
                            </label>
                            <span className={this.state.errors["customerContactId"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerContactId"]}</span>
                        </div>
                        <div className={!jobDetails.customerExists || jobDetails.customerCashSaleAccount ? "" : "hidden"}>
                            <div className={!jobDetails.customerExists ? "" : "hidden"}>
                                <hr></hr>
                                <label className="input-group" htmlFor="customer">
                                    <span className="label">Customer Company Name</span>
                                    <input className="input" type="text" maxLength={500} id="customer" name="customer" value={jobDetails.customer} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["customer"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customer"]}</span>
                                {customerAddress}
                                <span className={this.state.errors["customerAddress"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerAddress"]}</span>
                                <label className="input-group" htmlFor="customerAbn">
                                    <span className="label">Customer ABN (optional)</span>
                                    <input className="input" type="text" maxLength={20} id="customerAbn" name="customerAbn" value={jobDetails.customerAbn} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                            </div>
                            <label className="input-group" htmlFor="customerContactName">
                                <span className="label">Customer First/Last Name</span>
                                <div className="input-group--inline">
                                    <input className="input marginRight10" type="text" maxLength={25} id="customerContactFirstName" name="customerContactFirstName" value={jobDetails.customerContactFirstName} onChange={(e) => this.handleChange(e)}></input>
                                    <input className="input" type="text" maxLength={25} id="customerContactLastName" name="customerContactLastName" value={jobDetails.customerContactLastName} onChange={(e) => this.handleChange(e)}></input>
                                </div>
                            </label>
                            <span className={this.state.errors["customerContact"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerContact"]}</span>
                            <label className="input-group" htmlFor="customerContactPhone">
                                <span className="label">Customer Phone</span>
                                <input className="input" type="text" maxLength={30} id="customerContactPhone" name="customerContactPhone" value={jobDetails.customerContactPhone} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <span className={this.state.errors["customerContactPhone"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerContactPhone"]}</span>
                            <label className="input-group" htmlFor="customerContactEmail">
                                <span className="label">Customer Email</span>
                                <input className="input" type="text" maxLength={200} id="customerContactEmail" name="customerContactEmail" value={jobDetails.customerContactEmail} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <span className={this.state.errors["customerContactEmail"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerContactEmail"]}</span>
                            <label className={this.props.systemCompanyId === 3 && !jobDetails.customerExists ? "input-group" : "hidden"} htmlFor="useTyabbPricelist">
                                <span className="label">Use RAB/PTH Pricelist?</span>
                                <input className="checkbox" type="checkbox" id="useTyabbPricelist" name="useTyabbPricelist" checked={jobDetails.useTyabbPricelist} onChange={(e) => this.handleCheckboxChange(e)}></input>
                            </label>
                            <hr></hr>
                        </div>
                        <div className={jobDetails.priceOnly ? "hidden" : ""}>
                            <label className={jobDetails.id === 0 && (jobDetails.jobType === 2 || jobDetails.jobType === 3) ? "input-group" : "hidden"} htmlFor="sendEmailToCustomer">
                                <span className="label label--setsizesm">Send Confirmation Email</span>
                                <input className="checkbox" type="checkbox" id="sendEmailToCustomer" name="sendEmailToCustomer" checked={jobDetails.sendEmailToCustomer} onChange={(e) => this.handleCheckboxChange(e)} disabled={!this.state.canSendEmailToCustomer }></input>
                            </label>

                            <label className={jobDetails.customerContactId && jobDetails.customerContactId > 0 && jobDetails.id === 0 ? "input-group" : "hidden"} htmlFor="customerContactEmail">
                                <span className="label">Customer Email</span>
                                <input className="input marginRight10" type="text" maxLength={200} id="customerContactEmail" name="customerContactEmail" value={jobDetails.customerContactEmail} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <span className={this.state.errors["customerContactConfirmEmail"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["customerContactConfirmEmail"]}</span>

                            <div className={jobDetails.jobType === 2 && jobDetails.id === 0 ? "" : "hidden"}>
                                <div>
                                    <label className="input-group" htmlFor="useCustomerContact">
                                        <span className="label">Use Customer Contact</span>
                                        <input className="checkbox" type="checkbox" name="useCustomerContact" checked={this.state.useCustomerContact} onChange={(e) => this.handleUseCustomerContactChange(e)}></input>
                                    </label>
                                </div>
                                <div>
                                    <label className="input-group" htmlFor="siteContactName">
                                        <span className="label">Site Contact Name</span>
                                        <input className="input" type="text" maxLength={50} id="siteContactName" name="siteContactName" value={this.props.siteContacts.siteContactName} onChange={(e) => this.handleSiteContactChange(e)}></input>
                                    </label>
                                    <span className={this.state.errors["siteContactName"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["siteContactName"]}</span>
                                </div>
                                <div>
                                    <label className="input-group" htmlFor="siteContactPhone">
                                        <span className="label">Site Contact Phone</span>
                                        <input className="input" type="text" maxLength={50} id="siteContactPhone" name="siteContactPhone" value={this.props.siteContacts.siteContactPhone} onChange={(e) => this.handleSiteContactChange(e)}></input>
                                    </label>
                                    <span className={this.state.errors["siteContactPhone"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["siteContactPhone"]}</span>
                                </div>
                            </div>
                            <div className={jobDetails.jobType === 2 && jobDetails.id === 0 ? "" : "hidden"}>
                                <label className="input-group" htmlFor="siteContact2">
                                    <span className="label">Site Contact 2</span>
                                    <input className="input" type="text" maxLength={100} id="siteContact2" name="siteContact2" value={this.props.siteContacts.siteContact2} onChange={(e) => this.handleSiteContactChange(e)}></input>
                                </label>
                            </div>

                            <div className={!jobDetails.siteAddressExists ? "hidden" : ""}>
                                {googleContents}
                                <span className={this.state.errors["siteAddress"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["siteAddress"]}</span>
                                <div className="marginLeftLabel">Distance: {jobDetails.siteAddressDistanceFormatted} Duration: {jobDetails.siteAddressDurationFormatted}</div>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="siteAddrExists">
                                    <span className="label label--setsizesm">Site Address Doesn't Exist</span>
                                    <input className="checkbox" type="checkbox" id="siteAddrExists" name="siteAddressExists" checked={!jobDetails.siteAddressExists} onChange={(e) => this.handleAddressExistsChange(e)}></input>
                                </label>
                                <div className={jobDetails.siteAddressExists ? "hidden" : ""}>
                                    <label className="input-group" htmlFor="manualStreetName">
                                        <span className="label">Site Street No/Name</span>
                                        <input className="input" type="text" id="manualStreetName" name="manualStreetName" value={jobDetails.manualStreetName} onChange={(e) => this.handleChange(e)}></input>
                                    </label>
                                    <span className={this.state.errors["manualStreetName"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["manualStreetName"]}</span>

                                    <label className="input-group" htmlFor="manualSuburb">
                                        <span className="label">Site Suburb</span>
                                        <input className="input" type="text" maxLength={40} id="manualSuburb" name="manualSuburb" value={jobDetails.manualSuburb} onChange={(e) => this.handleChange(e)}></input>
                                    </label>
                                    <span className={this.state.errors["manualSuburb"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["manualSuburb"]}</span>

                                    <label className="input-group" htmlFor="manualPostCode">
                                        <span className="label">Site Post Code</span>
                                        <input className="input" type="text" id="manualPostCode" name="manualPostCode" value={jobDetails.manualPostCode} onChange={(e) => this.handleChange(e)}></input>
                                    </label>
                                    <span className={this.state.errors["manualPostCode"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["manualPostCode"]}</span>

                                    <label className="input-group" htmlFor="manualState">
                                        <span className="label">State</span>
                                        <input className="input" maxLength={5} type="text" id="manualState" name="manualState" value={jobDetails.manualState} onChange={(e) => this.handleChange(e)}></input>
                                    </label>
                                    <span className={this.state.errors["manualState"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["manualState"]}</span>
                                </div>
                            </div>
                            <div className={jobDetails.siteAddressExists ? "hidden" : ""}>
                                {googleAddressNearSite}
                                <span className="label errors marginLeftLabel">{this.state.errors["addressNearSiteAddress"]}</span>

                                <label className="input-group" htmlFor="addressSiteAddressDuration">
                                    <span className="label">Mins to Site Address (one way)</span>
                                    <input className="input" type="text" id="addressSiteAddressDuration" name="addressSiteAddressDuration" value={jobDetails.addressSiteAddressDuration} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <span className={this.state.errors["addressSiteAddressDuration"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["addressSiteAddressDuration"]}</span>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="unitno">
                                    <span className="label">Unit Number</span>
                                    <input className="input" type="text" maxLength={10} id="unitno" name="unitNumber" value={jobDetails.unitNumber} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="lotno">
                                    <span className="label">Lot Number</span>
                                    <input className="input" type="text" id="lotno" name="lotNumber" value={jobDetails.lotNumber} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                            </div>
                            <div className={this.props.systemCompanyId === 3 ? "" : "hidden"}>
                                <div className="marginLeftLabel">Enter any notes for delivery and/or placement of products for driver, <br></br> e.g. Shop Name, Access from [Street Name], Gate [Number] etc.</div>
                                <label className="input-group" htmlFor="deliveryPlacementNotes">
                                    <span className="label">Delivery/Placement Notes</span>
                                    <textarea className="input" rows={2} id="deliveryPlacementNotes" name="deliveryPlacementNotes" value={jobDetails.deliveryPlacementNotes} onChange={(e) => this.handleChange(e)}></textarea>
                                </label>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="jobtype">
                                    <span className="label">Job Type</span>
                                    <select className="select" id="jobtype" name="jobType" value={jobDetails.jobType} onChange={(e) => this.handleChange(e)} disabled={jobDetails.id === 0}>
                                        <option hidden defaultValue="-1"></option>
                                        {jobTypeOptions.map(jobType =>
                                            <option key={jobType.id} value={jobType.id}>{jobType.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={this.state.errors["jobType"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["jobType"]}</span>
                            </div>
                            <div className={jobDetails.id > 0 && jobDetails.accountTypeId === 1 ? "" : "hidden" }>
                                <label className="input-group" htmlFor="paymentType">
                                    <span className="label">Payment Method</span>
                                    <select className="select" id="paymentType" name="paymentType" value={jobDetails.paymentType} onChange={(e) => this.handleChange(e)} disabled={jobDetails.id === 0}>
                                        <option defaultValue="-1" value={-1}></option>
                                        <option value={3}>Credit Card</option>
                                        <option value={4}>Direct Debit</option>
                                    </select>
                                </label>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="gatecode">
                                    <span className="label">Gate Code</span>
                                    <input className="input" type="text" maxLength={20} id="gatecode" name="gateCode" value={jobDetails.gateCode} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <label className="input-group" htmlFor="reference">
                                    <span className="label">Reference</span>
                                    <input className="input" type="text" maxLength={200} id="reference" name="reference" value={jobDetails.reference} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                            </div>
                            <div className={this.props.systemCompanyId === 3 ? "hidden" : ""}>
                                <div>
                                    <label className="input-group" htmlFor="truck">
                                        <span className="label">Truck Access?</span>
                                        <select className="select" name="truckAccess" value={jobDetails.truckAccess} onChange={(e) => this.handleChange(e)}>
                                            <option hidden defaultValue="-1"></option>
                                            {yesNoOptions.map(yn =>
                                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                                            )};
                                        </select>
                                    </label>
                                    <span className={this.state.errors["truckAccess"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["truckAccess"]}</span>
                                </div>
                                <div>
                                    <label className="input-group" htmlFor="parking">
                                        <span className="label">Parking Avail?</span>
                                        <select className="select" id="parking" name="parking" value={jobDetails.parking} onChange={(e) => this.handleChange(e)}>
                                            <option hidden defaultValue="-1"></option>
                                            {yesNoOptions.map(yn =>
                                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                                            )};
                                        </select>
                                    </label>
                                    <span className={this.state.errors["parking"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["parking"]}</span>
                                </div>
                                <div>
                                    <label className="input-group" htmlFor="siteclean">
                                        <span className="label">Is Site {this.props.systemCompanyId === 2 ? "Clean" : "Level"}?</span>
                                        <select className="select" id="siteclean" name="siteClean" value={jobDetails.siteClean} onChange={(e) => this.handleChange(e)}>
                                            <option hidden defaultValue="-1"></option>
                                            {yesNoOptions.map(yn =>
                                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                                            )};
                                        </select>
                                    </label>
                                    <span className={this.state.errors["siteClean"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["siteClean"]}</span>
                                </div>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="powerlines">
                                    <span className="label">Powerlines within 4.6m?</span>
                                    <select className="select" id="powerlines" name="powerlinesWithinRange" value={jobDetails.powerlinesWithinRange} onChange={(e) => this.handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {yesNoOptions.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={this.state.errors["powerlinesWithinRange"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["powerlinesWithinRange"]}</span>
                            </div>
                            <div className={this.props.systemCompanyId === 3 ? "" : "hidden"}>
                                <label className="input-group" htmlFor="weekdayAccess">
                                    <span className="label">Is there clear access between 7am and 3pm weekdays?</span>
                                    <select className="select" id="weekdayAccess" name="weekdayAccess" value={jobDetails.weekdayAccess} onChange={(e) => this.handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {yesNoOptions.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={this.state.errors["weekdayAccess"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["weekdayAccess"]}</span>

                                <label className="input-group" htmlFor="weekendAccess">
                                    <span className="label">Is there weekend access?</span>
                                    <select className="select" id="weekendAccess" name="weekendAccess" value={jobDetails.weekendAccess} onChange={(e) => this.handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {yesNoOptions.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={this.state.errors["weekendAccess"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["weekendAccess"]}</span>

                                <label className="input-group" htmlFor="phoneCallRequired">
                                    <span className="label">Phone call required prior to arrival?</span>
                                    <select className="select" id="phoneCallRequired" name="phoneCallRequired" value={jobDetails.phoneCallRequired} onChange={(e) => this.handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {yesNoOptions.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={this.state.errors["phoneCallRequired"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["phoneCallRequired"]}</span>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="statusId">
                                    <span className="label">Status</span>
                                    <select className="select" id="statusId" name="statusId" value={jobDetails.statusId} onChange={(e) => this.handleChange(e)} disabled>
                                        {statusOptions.map(status =>
                                            <option key={status.id} value={status.id}>{status.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={this.state.errors["statusId"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["statusId"]}</span>
                            </div>
                        </div>
                        <div className={jobDetails.id > 0 && this.props.canUnlockJob && jobDetails.locked ? "" : "hidden"}>
                            <label className="input-group" htmlFor="goAheadOverride">
                                <span className="label label--setsizesm">Job can goahead (locked customer)</span>
                                <input className="checkbox" type="checkbox" id="goAheadOverride" name="goAheadOverride" checked={jobDetails.goAheadOverride} onChange={(e) => this.handleCheckboxChange(e)}></input>
                            </label>
                        </div>
                    </div>
                    <div className={existingJob || jobDetails.priceOnly ? 'hidden' : 'columnright' }>
                        <div>
                            <label className="input-group--nowrap" htmlFor="notes">
                                <span className="label">Notes</span>
                                <textarea className="input notes__stepSize" id="notes" name="notes" value={jobDetails.notes} onChange={(e) => this.handleChange(e)}></textarea>
                            </label>
                        </div>
                        <div className={this.props.copyJob && this.props.systemCompanyId === 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="copyQuote">
                                <span className="label">Copy Quote</span>
                                <input className="input checkbox__Size" type="checkbox" id="copyQuote" name="copyQuote" checked={jobDetails.copyQuote} onChange={(e) => this.handleCheckboxChange(e)}></input>
                            </label>
                        </div>
                        <div className={this.props.copyJob ? "" : "hidden"}>
                            <label className="input-group" htmlFor="linkJob">
                                <span className="label">Link Job {this.props.jobDetails.copyJobId}</span>
                                <input className="input checkbox__Size" type="checkbox" id="linkJob" name="linkJob" checked={jobDetails.linkJob} onChange={(e) => this.handleCheckboxChange(e)}></input>
                            </label>
                        </div>
                    </div>
                </div>

                <div className={jobDetails.id === 0 ? "defaultbutton__container--right marginBottom10" : "marginBottom10"}>
                    <button className={jobDetails.id == 0 || jobDetails.statusId == 7 ? "hidden" : "defaultbutton-prev"} type="button" onClick={this.showCancelModal} disabled={!this.props.canCancelJob}>Cancel Job</button>
                    <button className={jobDetails.id > 0 && jobDetails.statusId == 7 && this.props.canReopenJob ? "defaultbutton-prev" : "hidden"} type="button" onClick={(e) => this.reopenJob(e)}>Re-open Job</button>
                    <button className="defaultbutton--save marginLeft10" type="button" onClick={this.contactSubmit}>{buttonName}</button>
                </div>

            </div>
        );
    }
}
